import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import type { ColDef } from "ag-grid-community";
import BaseTemplate from "../components/BaseTemplate";
import PersistentGrid from "../components/PersistentGrid/PersistentGrid";
import PersistentGridResetButton from "../components/PersistentGrid/PersistentGridResetButton";
import PersistentGridClearFiltersButton from "../components/PersistentGrid/PersistentGridClearFiltersButton";
import { PersistentGridProvider } from "../contexts/PersistentGridContext";
import { useApi } from "../hooks/useApi";
import { NotificationContext } from "../components/NotificationPane";
import type { NotificationContextType, OpenPassword as OpenPasswordRow } from "../types";
import InternalUserRoute from "../util/internal_user_route";
import { buildDefaultColDef, EDB_CUSTOM_withHeaderTooltips } from "../util/AgGridFunctions";
import { useColumnDefaults } from "../hooks/useColumnDefaults";

// Matches the doc.open_password column width; the proc re-checks this server-side.
const PASSWORD_MAX_LENGTH = 30;

// doc.open_password - the candidate passwords edb_megaloader tries against encrypted
// files. Deliberately view + add only: no row click, no edit, no delete. success_counter is
// maintained by those consumers, so it is shown (and sortable) but never editable here.
function OpenPassword() {
  const { getOpenPasswords, addOpenPassword } = useApi();
  const columnDefaults = useColumnDefaults();
  const { pushNotification } =
    useContext<NotificationContextType | null>(NotificationContext)!;
  const [rowData, setRowData] = useState<OpenPasswordRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { isOpen: isAddOpen, onOpen: onAddOpen, onClose: onAddClose } = useDisclosure();
  const [newPassword, setNewPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const loadPasswords = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getOpenPasswords();
      setRowData(data);
      setError(null);
    } catch (err) {
      console.error("Error loading open passwords", err);
      setError("Failed to load passwords.");
      pushNotification("Failed to load passwords.", "error");
    } finally {
      setLoading(false);
    }
  }, [getOpenPasswords, pushNotification]);

  useEffect(() => {
    loadPasswords();
  }, [loadPasswords]);

  const columnDefs = useMemo<ColDef<OpenPasswordRow>[]>(
    () => EDB_CUSTOM_withHeaderTooltips([
      { field: "sk", headerName: "SK", ...buildDefaultColDef(columnDefaults, "sk"), minWidth: 100, maxWidth: 140 },
      // Default sort: alphabetical (the proc already returns rows this way; the grid pins it so
      // a saved column state can't silently lose it). AG Grid's default string comparator is
      // case-sensitive (all uppercase before any lowercase) - hence the explicit comparator.
      {
        field: "password", headerName: "Password", ...buildDefaultColDef(columnDefaults, "password"), minWidth: 220, sort: "asc",
        comparator: (a: string | null, b: string | null) => (a ?? "").localeCompare(b ?? "", undefined, { sensitivity: "base" }),
      },
      { field: "success_counter", headerName: "Success Count", ...buildDefaultColDef(columnDefaults, "success_counter"), minWidth: 160, type: "numericColumn" },
    ]),
    [columnDefaults],
  );

  const defaultColDef = useMemo<ColDef<OpenPasswordRow>>(
    () => ({
      ...buildDefaultColDef(columnDefaults),
      sortable: true,
      resizable: true,
      filter: true,
      floatingFilter: true,
      minWidth: 100,
      flex: 1,
    }),
    [columnDefaults],
  );

  const trimmed = newPassword.trim();
  const tooLong = trimmed.length > PASSWORD_MAX_LENGTH;
  const duplicate = rowData.some((r) => r.password.toLowerCase() === trimmed.toLowerCase());
  const canSave = trimmed.length > 0 && !tooLong && !duplicate && !saving;

  const openAddModal = () => {
    setNewPassword("");
    onAddOpen();
  };

  const handleAddCancel = () => {
    setNewPassword("");
    onAddClose();
  };

  const handleAddSave = async () => {
    if (!canSave) return;
    try {
      setSaving(true);
      await addOpenPassword(trimmed);
      await loadPasswords();
      pushNotification("Password added.", "success");
      handleAddCancel();
    } catch (err) {
      console.error("Error adding password", err);
      // The backend returns the proc's own message for input rejections (400) - show that.
      const message = (err as Error)?.message || "Failed to add password.";
      pushNotification(message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <InternalUserRoute>
      <PersistentGridProvider gridKey="doc-open-password-grid">
        <BaseTemplate fitToWindow={true}>
          <BaseTemplate.Header>
            <HStack>
              <PersistentGridResetButton />
              <PersistentGridClearFiltersButton />
              <Button onClick={openAddModal}>Add</Button>
              {error && <Text color="red.500">{error}</Text>}
            </HStack>
          </BaseTemplate.Header>
          <BaseTemplate.Body>
            <Box w="100%" h="100%">
              <div className="ag-theme-quartz" style={{ width: "100%", height: "100%" }}>
                <PersistentGrid<OpenPasswordRow>
                  rowData={rowData}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  loading={loading}
                  animateRows
                />
              </div>
            </Box>
          </BaseTemplate.Body>
        </BaseTemplate>
      </PersistentGridProvider>

      <Modal isOpen={isAddOpen} onClose={handleAddCancel}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Password</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isInvalid={tooLong || duplicate}>
              <FormLabel>Password</FormLabel>
              <Input
                autoFocus
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleAddSave();
                }}
                maxLength={PASSWORD_MAX_LENGTH + 10}
                autoComplete="off"
                spellCheck={false}
              />
              <FormHelperText>
                {duplicate
                  ? "That password is already in the list."
                  : tooLong
                    ? `Maximum ${PASSWORD_MAX_LENGTH} characters.`
                    : `Stored exactly as typed (up to ${PASSWORD_MAX_LENGTH} characters); leading/trailing spaces are removed.`}
              </FormHelperText>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleAddCancel}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleAddSave} isDisabled={!canSave} isLoading={saving}>
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </InternalUserRoute>
  );
}

export default OpenPassword;
